import styled from 'styled-components';
import { GlobalToken } from 'antd';

import { CustomThemeToken } from '@/theme';

interface OperationParametersRowProps {
  $hasInletValve: boolean;
  $hasBypassValve: boolean;
  theme: { token: GlobalToken };
}

export const OperationParametersRow = styled.div<OperationParametersRowProps>`
  display: flex;
  align-items: center;
  align-self: stretch;
  justify-content: space-between;
  padding: ${({ theme: { token } }) => token.margin}px;

  .alerts-container {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
  .alerts {
    gap: ${({ theme: { token } }) => token.marginXS}px;
  }

  .valves-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${({ theme: { token } }) => token.marginSM}px;
    white-space: nowrap;
  }

  .operation-parameter {
    height: 100%;
    display: flex;
    gap: ${({ theme: { token } }) => token.marginXS}px;
    align-items: center;

    .operation-parameter-title {
      font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
      color: ${({ theme: { token } }) => token.colorTextSecondary};
    }

    .operation-parameter-value {
      display: flex;
      align-items: center;
      color: ${({ theme: { token } }) => token.colorTextBase};
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      line-height: ${({ theme: { token } }) => token.lineHeightLG}px;
    }
  }

  .right-border {
    width: 1px;
    background-color: ${({ theme: { token } }) => token.colorBgBase};
    z-index: 1;
    height: 100%;
    justify-self: end;
  }
`;

export const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 400px;

  .node-widget-container {
    margin: 0;
  }
`;

interface ContainerProps {
  $isPoweredOn: boolean;
  $isInMaintenance: boolean;
  theme: { token: CustomThemeToken };
}

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background-color: ${({ $isPoweredOn, $isInMaintenance, theme: { token } }) =>
    !$isPoweredOn
      ? token.Ecoplant_Compressor.colorBGStopped
      : $isInMaintenance
      ? token.Ecoplant_Compressor.colorBGMaintenance
      : token.colorBgContainer};
  border: ${({ $isPoweredOn, $isInMaintenance, theme: { token } }) =>
    !$isPoweredOn
      ? `2px dashed ${token.colorErrorBorder}`
      : $isInMaintenance
      ? `2px dashed ${token.colorWarningBorder}`
      : 'none'};
  border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
  overflow: hidden;

  .horizontal-border {
    height: 1px;
    width: 100%;
    background-color: ${({ theme: { token } }) => token.colorBorderSecondary};
  }

  .toggle-status-box {
    min-width: 92px;
  }

  .bottom-padding {
    padding: 0 ${({ theme: { token } }) => token.paddingXS}px;
  }

  .top-padding {
    padding: ${({ theme: { token } }) => token.paddingXS}px ${({ theme: { token } }) => token.padding}px;
  }

  .top-row {
    position: relative;
    display: flex;
    align-items: center;
    gap: ${({ theme: { token } }) => token.marginXS}px;
    align-self: stretch;
    border-radius: ${({ theme: { token } }) => token.borderRadius}px ${({ theme: { token } }) => token.borderRadius}px 0
      0;
    min-height: ${({ theme: { token } }) => token.marginXL * 2}px;

    .compressor-type-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: ${({ theme: { token } }) => token.marginXXS}px;
      flex-shrink: 0;

      .compressor-type-icon {
        width: auto;
      }

      .compressor-type-label {
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        color: ${({ theme: { token } }) => token.colorTextSecondary};
      }
    }

    .compressor-title {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      flex: 1 1 0%;
      min-width: 0;
      gap: ${({ theme: { token } }) => token.marginXXS}px;

      .name {
        font-size: ${({ theme: { token } }) => token.fontSize}px;
        color: ${({ theme: { token } }) => token.colorText};
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      }

      .model {
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        color: ${({ theme: { token } }) => token.colorTextSecondary};
      }
    }

    .connectivity-icon-wrapper {
      width: auto;
      height: 100%;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: ${({ theme: { token } }) => token.marginXS}px;

      .icons-row {
        display: flex;
        flex-direction: row;
        gap: ${({ theme: { token } }) => token.marginXS}px;
        align-items: center;
        justify-content: center;

        > svg {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        > div {
          width: 24px;
          height: 24px;
          flex-shrink: 0;
        }
      }

      .status-icon {
        display: flex;
        justify-content: center;
        height: 50%;
      }

      .status-name {
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        color: ${({ theme: { token } }) => token.colorTextSecondary};
      }
    }
  }

  .middle-row-container {
    display: flex;
    flex-direction: column;
    min-height: ${({ theme: { token } }) => token.marginXXL * 4}px;
  }

  .middle-row {
    display: flex;
    flex-direction: column;

    .details-widget-slider {
      padding: ${({ theme: { token } }) => token.padding}px ${({ theme: { token } }) => token.padding}px 52px
        ${({ theme: { token } }) => token.padding}px;

      .slider-container {
        padding: 0 ${({ theme: { token } }) => token.paddingXL}px;
        max-height: ${({ theme: { token } }) => token.marginMD + token.marginLG}px;
        max-width: 85%;
        .value-arrow-block {
        }

        .value {
          margin-bottom: 0;

          .value-content {
            font-size: ${({ theme: { token } }) => token.fontSizeXL}px;
          }

          .units-sign {
            bottom: 4px;
          }
        }

        .slider {
          &:before {
            margin-left: -16.2601626%;
            width: 16.2601626%;
            border-top-left-radius: ${({ theme: { token } }) => token.borderRadiusXS}px;
            border-bottom-left-radius: ${({ theme: { token } }) => token.borderRadiusXS}px;
          }

          &:after {
            margin-right: -16.2601626%;
            width: 16.2601626%;
            border-top-right-radius: ${({ theme: { token } }) => token.borderRadiusXS}px;
            border-bottom-right-radius: ${({ theme: { token } }) => token.borderRadiusXS}px;
          }
        }

        .triangle {
          height: 8px;
          width: 12px;
          margin-bottom: ${({ theme: { token } }) => token.marginXXS}px;
        }

        .points {
          .point-value {
            font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          }

          .label {
            font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          }
        }
      }
    }
  }

  .bottom-row {
    display: flex;
    align-items: center;
    align-self: stretch;
    border-radius: 0 0 ${({ theme: { token } }) => token.borderRadiusLG}px
      ${({ theme: { token } }) => token.borderRadiusLG}px;

    .bottom-left-container {
      display: flex;
      justify-content: flex-start;
      flex-grow: 1;
      margin-bottom: ${({ theme: { token } }) => token.marginXXS}px;
      margin-top: ${({ theme: { token } }) => token.marginXS}px;

      .flex-row-centered {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: ${({ theme: { token } }) => token.marginXXS}px;
      }

      .metric-container {
        padding-right: ${({ theme: { token } }) => token.paddingXXS}px;
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};

        .rpm-text,
        .power-text {
          font-size: ${({ theme: { token } }) => token.fontSize}px;
          color: ${({ theme: { token } }) => token.colorTextSecondary};
          font-weight: ${({ theme: { token } }) => token.fontWeightNormal};
          line-height: ${({ theme: { token } }) => token.lineHeight};
          text-align: left;
        }

        .rpm-value-box,
        .power-value-box {
          display: flex;
          align-items: flex-start;
          gap: ${({ theme: { token } }) => token.marginXXS / 2}px;

          .rpm-value {
            font-size: ${({ theme: { token } }) => token.fontSize}px;
            color: ${({ theme: { token } }) => token.colorText};
          }

          .power-value {
            font-size: ${({ theme: { token } }) => token.fontSize}px;
            color: ${({ theme: { token } }) => token.colorText};
          }

          .rpm-unit,
          .power-unit {
            font-size: ${({ theme: { token } }) => token.fontSize}px;
            color: ${({ theme: { token } }) => token.colorText};
          }

          .power-percentage {
            font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
            color: ${({ theme: { token } }) => token.colorText};
          }
        }
      }

      .flex-column-centered {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
    }

    .vertical-border {
      width: 1px;
      background-color: ${({ theme: { token } }) => token.colorBorderSecondary};
      z-index: 1;
      align-self: stretch;
      margin-right: ${({ theme: { token } }) => token.paddingXS}px;
    }
    .start-stop-buttons-container {
      .flex-row-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: ${({ theme: { token } }) => token.paddingXS}px;
        gap: ${({ theme: { token } }) => token.marginXS}px;

        .color-success {
          background: ${({ theme: { token } }) => token.colorSuccess};
          &:hover {
            background: ${({ theme: { token } }) => token.colorSuccessHover};
          }
        }

        button {
          > * {
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      }
    }
  }
`;

export const AlertsContainer = styled.div<{ theme: { token: GlobalToken } }>`
  height: 100%;
  display: flex;
  flex-direction: row;
  width: auto;
  align-items: center;
  justify-content: flex-start;

  .alerts {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    .alert-icon-count-container {
      display: flex;
      gap: ${({ theme: { token } }) => token.marginXS}px;
      align-items: center;

      .alert-icon {
        display: flex;
        align-items: center;

        svg {
          width: 18px;
          height: 18px;
        }
      }

      .alert-count {
        font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
        color: ${({ theme: { token } }) => token.colorText};
      }
    }
  }
`;
